/**
 * Queue counters for the sidebar badges (`GET /api/admin/queues/`, SPEC §5.10).
 *
 * Shared by the shell and every feature: after an action that changes a queue (approve/reject
 * verification, close complaint, block/unblock, dismiss flags, remove shipment) invalidate
 * `queueKeys.all` so the badges update immediately instead of on the next 30 s poll.
 */
import { useQuery } from '@tanstack/react-query';

import { http } from './http';
import type { AdminQueues } from './types';

export const QUEUES_POLL_INTERVAL_MS = 30_000;

export const queueKeys = {
  all: ['admin', 'queues'] as const,
};

export async function fetchAdminQueues(signal?: AbortSignal): Promise<AdminQueues> {
  const { data } = await http.get<AdminQueues>('/admin/queues/', { signal });
  return data;
}

/**
 * Polls the counters every 30 s while the tab is visible. Failures are deliberately silent:
 * a missing badge must never break the shell.
 */
export function useAdminQueues() {
  return useQuery({
    queryKey: queueKeys.all,
    queryFn: ({ signal }) => fetchAdminQueues(signal),
    refetchInterval: QUEUES_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: QUEUES_POLL_INTERVAL_MS / 2,
    retry: false,
  });
}
